import axios from 'axios';
import { Box } from '@mui/material';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import ActivityLogs from 'src/content/applications/activitylogs';
import { useAdminWorkspace } from './AdminWorkspaceProvider';
import { useCompanyProfileWorkspaceScope } from './CompanyProfileWorkspaceScope';

function ActivityFrame({ basePath, actorId }: { basePath: string; actorId: number }) {
  const frame = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number>();
  const source = useMemo(() => ({
    key: `${basePath}:${actorId}`,
    list: async () => {
      const response = await axios.get(basePath);
      if (!Array.isArray(response.data?.data)) throw new Error('Schedule activity unavailable');
      return response;
    },
    recipients: (id: number) => axios.get(`${basePath}/${id}/recipients`),
    cancel: (id: number) => axios.post(`${basePath}/${id}/cancel`)
  }), [basePath, actorId]);
  useLayoutEffect(() => {
    const resize = () => setHeight(Math.max(0, window.innerHeight - (frame.current?.getBoundingClientRect().top ?? 0)));
    resize(); window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);
  return <Box ref={frame} role="region"
aria-label="Scheduled activity" tabIndex={0}
    sx={{ height: height ?? '100%', minHeight: 0, overflowY: 'auto' }}>
    <ActivityLogs key={source.key} dataSource={source} />
  </Box>;
}
export function PlatformScheduleActivityPage() {
  const state = useAdminWorkspace();
  if (state.kind !== 'platform') return <Navigate to="/status/404" replace />;
  return <ActivityFrame basePath="/platform/scheduled-activity" actorId={state.principal.id} />;
}
export function CompanyScheduleActivityPage() {
  const scope = useCompanyProfileWorkspaceScope();
  const { companyId } = useParams();
  const levels = scope?.accessLevels?.length ? scope.accessLevels : [scope?.accessLevel];
  if (!scope || Number(companyId) !== scope.company.id || !levels.includes('COMPANY_ADMIN'))
    return <Navigate to="/status/404" replace />;
  return <ActivityFrame basePath={`/companies/${scope.company.id}/scheduled-activity`} actorId={scope.actorId} />;
}
